import React, { useEffect, useRef, useState } from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import { useNavigate } from "react-router-dom";
import classes from "./Login.module.css";

import logo from "../../../../src/assets/images/logo_black.png";
import homeRightGraphic from "../../../../src/assets/images/ASTT Login Graphic.svg";
import Grid from "@mui/material/Grid";
import { Button } from "@mui/material";
import RequestForAccess from "components/RequestForAccess/RequestForAccess";
import { REQUEST_ACCESS } from "../../CONSTANTS";
import SnackBar from "components/SnackBar/SnackBar";
import { useDispatch } from "react-redux";
import { SnackBarStoreAction } from "Store/SnackBar";
import { useAuth } from "context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const oidcAuth = useOidcAuth();
  const [loggedIn, setLoggedIn] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const { roles, isLoaded } = useAuth();

  const dispatch = useDispatch();
  const redirectUrlRef = useRef(loginFailedUrl());

  const checkPermission = (access) => {
    const allPermissions = [
      "General_Limited_Group",
      "PII_Group",
      "PII_Group_Low",
      "PII_Group_Med",
      "PII_Group_FULL",
      "Ops_Group",
      "OG_Group",
      "LD_Group",
      "LD_Group_Admin",
      "TA_Group",
      "WFM_Group",
      "CP_Group",
      "ISU_PRV_Access",
      "ASD_Group",
    ];

    const accessArray = access ? access.split(",") : [];

    return allPermissions.filter((permission) =>
      accessArray.includes(permission),
    );
  };

  const showSnack = (message, severity) => {
    dispatch(
      SnackBarStoreAction.updateSnack({ open: true, message, severity }),
    );
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({ open: false, message: "", severity: "" });
  };

  useEffect(() => {
    const noAccess = localStorage.getItem("noAccess");
    const sessionExpired = localStorage.getItem("sessionExpired");

    if (noAccess) {
      localStorage.removeItem("noAccess");
      showSnack(
        "No Access to this application, please contact your admin.",
        "info",
      );
    } else if (sessionExpired) {
      localStorage.removeItem("sessionExpired");
      showSnack("Session expired, please login again.", "info");
    }
  }, []);

  const clearLoginFailedStorage = () => {
    const keys = [
      "LPHome",
      "OBTPII",
      "CreateMilestone",
      "MileStones",
      "OBTInfo",
      "SearchRoster",
      "ContactRoster",
      "failedUrl",
    ];

    keys.forEach((key) => {
      if (localStorage.getItem(key)) localStorage.removeItem(key);
    });
  };

  function loginFailedUrl() {
    if (localStorage.getItem("LPHome")) return localStorage.getItem("LPHome");
    else if (localStorage.getItem("OBTPII"))
      return localStorage.getItem("OBTPII");
    else if (localStorage.getItem("CreateMilestone"))
      return localStorage.getItem("CreateMilestone");
    else if (localStorage.getItem("MileStones"))
      return localStorage.getItem("MileStones");
    else if (localStorage.getItem("OBTInfo"))
      return localStorage.getItem("OBTInfo");
    else if (localStorage.getItem("SearchRoster"))
      return localStorage.getItem("SearchRoster");
    else if (localStorage.getItem("ContactRoster"))
      return localStorage.getItem("ContactRoster");
    else if (
      localStorage.getItem("failedUrl") &&
      !localStorage.getItem("failedUrl").includes("bulkedit")
    )
      return localStorage.getItem("failedUrl");
    return "/welcome";
  }

  // Handle the redirect response from PingOne. react-oidc-context's
  // AuthProvider already processes the ?code=&state= callback internally
  // (via onSigninCallback in MslConfig.js, which just cleans up the URL) —
  // this effect reacts once a freshly authenticated user is available,
  // covering the same ground the old MSAL handleRedirectPromise callback did.
  useEffect(() => {
    if (!oidcAuth.isAuthenticated || !oidcAuth.user) return;

    try {
      // Confirmed via a live PPE login (2026-09-04): memberOf, email, and
      // given_name are on the ID token (react-oidc-context's user.profile),
      // NOT the access token — the access token's granted scope came back
      // as "openid" only, carrying no group claims at all. family_name and
      // a Microsoft-style unique_name claim were both absent from that
      // token, so those stay in the fallback chain below rather than being
      // assumed present.
      const profile = oidcAuth.user.profile ?? {};
      const roles = profile.memberOf || profile.roles || [];
      const rolesString = Array.isArray(roles)
        ? roles.join(",")
        : roles.toString();

      // Display/identity values — kept for UI use across the app
      // ASTT-7319 follow-up: PingOne's given_name/family_name claims (on
      // this app's ID token) appear to be populated by naively splitting a
      // single "Last, First" display-name attribute on the first space,
      // rather than true separate first/last-name attributes -- inferred
      // from a live DEV login producing a "User" value of
      // "Memon, Mohammedrizwan" (i.e. given_name="Memon," family_name=
      // "Mohammedrizwan", consistent with that split, though not yet
      // confirmed against the actual decoded ID token). Detecting that
      // shape (a trailing comma on given_name) and reordering to
      // "First Last" keeps this matching the format LP_DTRTrainingNotes/
      // LP_HandOffReport's ModifiedBy column has always stored (see
      // usp_Lp_GetRecentRostersForUser), so Recent Roster(s) name-matching
      // keeps working. Falls through to the original given+family order
      // untouched for any config where these really are separate claims
      // (e.g. if PingOne's mapping is corrected upstream later) -- this
      // only changes behavior for the specific "trailing comma" shape
      // actually observed.
      const rawGivenName = (profile.given_name ?? "").trim();
      const rawFamilyName = (profile.family_name ?? "").trim();
      const displayName = rawGivenName.endsWith(",")
        ? `${rawFamilyName} ${rawGivenName.slice(0, -1)}`.trim()
        : `${rawGivenName} ${rawFamilyName}`.trim();

      localStorage.setItem("User", displayName);
      localStorage.setItem(
        "EmailId",
        profile?.email ??
          profile?.unique_name ??
          profile?.preferred_username ??
          profile?.sub ??
          "",
      );

      const permissions = checkPermission(rolesString);

      if (permissions.length > 0) {
        setLoggedIn(true);
        const redirectUrl = redirectUrlRef.current;
        clearLoginFailedStorage();
        navigate(redirectUrl, { replace: true });
      } else {
        localStorage.setItem("noAccess", "true");
        navigate("/", { replace: true });
      }
    } catch (error) {
      console.error("Post-login handling error:", error);
      showSnack("Unable to login, Authentication error occurred.", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oidcAuth.isAuthenticated, oidcAuth.user, navigate]);

  // Surface auth errors explicitly instead of failing silently — this is
  // the recoverable-error-state design goal called out for this migration,
  // replacing what used to be a silent redirect loop in the old MSAL flow.
  useEffect(() => {
    if (oidcAuth.error) {
      console.error("PingOne auth error:", oidcAuth.error);
      showSnack("Unable to login, Authentication error occurred.", "error");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oidcAuth.error]);

  // Check for existing session using AuthContext roles (replaces localStorage("Access") read).
  // AuthContext reads the current PingOne access token — this is what extends the session.
  // isLoaded guards against navigating before that read completes.
  useEffect(() => {
    if (oidcAuth.isAuthenticated && !loggedIn && isLoaded) {
      if (roles.length > 0) {
        setLoggedIn(true);
        const redirectUrl = redirectUrlRef.current;
        clearLoginFailedStorage();
        navigate(redirectUrl, { replace: true });
      }
    }
  }, [oidcAuth.isAuthenticated, loggedIn, navigate, roles, isLoaded]);

  const handleLogin = () => {
    if (!oidcAuth.isLoading) {
      oidcAuth.signinRedirect().catch((error) => {
        console.error("Login redirect error:", error);
        showSnack("Unable to login, Authentication error occurred.", "error");
      });
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.body}>
        <div className={`flex-display align-center ${classes.header}`}>
          <div className={"logo-section"}>
            <div>
              <img alt="logo" width="75px" src={logo} />
            </div>
            <div className={classes.vectorLayout}>|</div>
          </div>
          <span className={[classes.title, classes.titleLayout].join(" ")}>
            Agent State Tracker
          </span>
        </div>

        <Grid
          className={classes.gridContainer}
          container
          direction="row"
          justifyContent="space-evenly"
          alignItems="center"
        >
          <Grid item xs={1}></Grid>
          <Grid item xs={5}>
            <div className={classes.leftContainer}>
              <span className={classes.titleWelcome}>Welcome!</span>
              <span className={classes.txtSignIn}>
                Sign in with your organizational account
              </span>
              {!loggedIn ? (
                <Button
                  className={classes.buttonSSO}
                  onClick={handleLogin}
                  disabled={oidcAuth.isLoading}
                >
                  {oidcAuth.isLoading ? "Logging in..." : "Login with SSO"}
                </Button>
              ) : null}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPopup(true);
                }}
              >
                Having issues logging in?
              </a>
              <RequestForAccess
                open={showPopup}
                handleClose={() => setShowPopup(false)}
              ></RequestForAccess>
            </div>
          </Grid>
          <Grid item xs={5.9} className={classes.rightContainer}>
            <img
              className={classes.homeGraphic}
              src={homeRightGraphic}
              alt="TTEC"
            />
          </Grid>
          <Grid item xs={0.1}></Grid>
        </Grid>
      </div>
      <SnackBar snack={snack} snackReset={snackReset} />
    </div>
  );
}
